import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, Phone, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [orderCounts, setOrderCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });

  const fetchAll = async () => {
    try {
      const [{ data: custs }, { data: orders }] = await Promise.all([
        api.get("/customers"),
        api.get("/orders"),
      ]);
      setCustomers(custs);
      const counts = {};
      orders.forEach((o) => {
        counts[o.customer_id] = (counts[o.customer_id] || 0) + 1;
      });
      setOrderCounts(counts);
    } catch (err) {
      console.error("Failed to fetch customers:", err);
      setCustomers([]);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const startEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone || "", email: c.email || "", address: c.address || "", notes: c.notes || "" });
    setOpen(true);
  };

  const startNew = () => {
    setEditing(null);
    setForm({ name: "", phone: "", email: "", address: "", notes: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Nama wajib diisi");
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer diperbarui");
      } else {
        await api.post("/customers", form);
        toast.success("Customer ditambahkan");
      }
      setOpen(false);
      fetchAll();
    } catch (e) {
      toast.error("Gagal menyimpan");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Hapus customer "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer dihapus");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-[1400px]" data-testid="customers-page">
      <header className="flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule">
        <div>
          <div className="clf-label mb-2">Customer</div>
          <h1 className="text-3xl tracking-tight font-medium">Daftar Customer</h1>
          <p className="text-sm text-stone-500 mt-1">
            Total: {customers.length} customer terdaftar.
          </p>
        </div>
        <button onClick={startNew} data-testid="new-customer-btn"
          className="bg-[#16202B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#243345] flex items-center gap-2">
          <Plus className="h-4 w-4" strokeWidth={2} /> Customer Baru
        </button>
      </header>

      {customers.length === 0 ? (
        <div className="bg-white border border-dashed border-[#D9CDB5] p-16 text-center">
          <p className="text-stone-500 mb-4">Belum ada customer.</p>
          <button onClick={startNew} className="text-[#A63D30] hover:text-[#8C3125] font-medium text-sm">
            + Tambah customer pertama
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((c) => (
            <div key={c.id}
              data-testid={`customer-card-${c.id}`}
              className="bg-white border border-[#E5DCC7] p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-3">
                <Link to={`/customers/${c.id}`} className="min-w-0 flex-1 group">
                  <div className="font-medium text-stone-900 truncate group-hover:text-[#A63D30]">
                    {c.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 mt-1">
                    {orderCounts[c.id] || 0} order
                  </div>
                </Link>
                <button onClick={() => del(c)} className="text-stone-400 hover:text-red-600 ml-2">
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="space-y-1.5 text-xs text-stone-600">
                {c.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-3 w-3" strokeWidth={1.5} /> {c.phone}
                  </div>
                )}
                {c.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-3 w-3" strokeWidth={1.5} /> {c.email}
                  </div>
                )}
                {c.address && (
                  <div className="text-stone-500 mt-2 line-clamp-2">{c.address}</div>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-stone-100 flex gap-2">
                <Link to={`/customers/${c.id}`} data-testid={`view-customer-${c.id}`}
                  className="text-xs text-stone-700 hover:text-[#A63D30] font-medium">
                  Lihat Detail
                </Link>
                <span className="text-stone-300">·</span>
                <button onClick={() => startEdit(c)} data-testid={`edit-customer-${c.id}`}
                  className="text-xs text-stone-700 hover:text-[#A63D30] font-medium">Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-[#D9CDB5]">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Customer" : "Customer Baru"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="clf-label block mb-1">Nama *</label>
              <input data-testid="cust-form-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value.replace(/(^\w|\s\w)/g, m => m.toUpperCase()) })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="clf-label block mb-1">Telp / WA</label>
                <input data-testid="cust-form-phone" inputMode="numeric" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
                  className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
              </div>
              <div>
                <label className="clf-label block mb-1">Email</label>
                <input data-testid="cust-form-email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
              </div>
            </div>
            <div>
              <label className="clf-label block mb-1">Alamat</label>
              <textarea data-testid="cust-form-address" value={form.address} rows={2}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <div>
              <label className="clf-label block mb-1">Catatan</label>
              <textarea data-testid="cust-form-notes" value={form.notes} rows={2}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-[#D9CDB5] hover:bg-stone-100">Batal</button>
            <button onClick={save} data-testid="cust-form-save"
              className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345]">
              Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
